import { ComponentType, lazy, LazyExoticComponent } from 'react';

/** One auto-reload per tab after a missing hashed JS chunk (stale deploy). */
const LAZY_CHUNK_RELOAD_SESSION_KEY = 'erp_lazy_chunk_reload';

const isChunkLoadFailure = (err: unknown): boolean => {
  const e = err as { name?: string; message?: string };
  const msg = String(e?.message || err || '');
  return e?.name === 'ChunkLoadError'
    || /Loading chunk [\d]+ failed/i.test(msg)
    || /Failed to fetch dynamically imported module/i.test(msg)
    || /Importing a module script failed/i.test(msg);
};

const clearLazyChunkReloadFlag = (): void => {
  try {
    sessionStorage.removeItem(LAZY_CHUNK_RELOAD_SESSION_KEY);
  } catch {
    // sessionStorage may be blocked
  }
};

/**
 * Like React.lazy, but after a deploy the cached main bundle can point at
 * hashed chunks that no longer exist — reload once so the tab picks up the
 * new index/main (manual reload already fixes this for users).
 * Flag clears only after a successful chunk load (avoids reload loops).
 */
export const lazyWithChunkReload = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> => (
  lazy(() => factory()
    .then((mod) => {
      clearLazyChunkReloadFlag();
      return mod;
    })
    .catch((err: unknown) => {
      if (typeof window !== 'undefined' && isChunkLoadFailure(err)) {
        try {
          if (sessionStorage.getItem(LAZY_CHUNK_RELOAD_SESSION_KEY) !== '1') {
            sessionStorage.setItem(LAZY_CHUNK_RELOAD_SESSION_KEY, '1');
            window.location.reload();
            // Hang until the browser reloads — do not surface the chunk error.
            return new Promise<{ default: T }>(() => {});
          }
        } catch {
          window.location.reload();
          return new Promise<{ default: T }>(() => {});
        }
      }
      throw err;
    }))
);
